import { Button } from 'blaze-ui/Button'
import Styles from './BrandVisualStyleMedia.module.scss'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useDeleteMediaFile } from 'hooks/queries/mediaFiles/useDeleteMediaFile'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useEffect, useState } from 'react'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { MediaFilesGrid } from './MediaFilesGrid'
import { UploadMoreButton } from './UploadMoreButton'
import cx from 'classnames'
import { GoogleDriveImport } from './GoogleDriveImport'
import { MediaPriorityToggle } from './MediaPriorityToggle'
import { MediaQualityIndicator } from './MediaQualityIndicator'
import { BrandKitMediaPriority } from 'types'
import { useIsOnboardingRedesignV2 } from 'hooks/useIsOnboardingRedesignV2'

export interface BrandVisualStyleMediaProps {
  onNext: (optIn: boolean) => void
  onBack: () => void
  brandKitId: number
  forVisualStyles?: boolean
}

export const BrandVisualStyleMedia = ({
  onNext,
  onBack,
  brandKitId,
  forVisualStyles = false,
}: BrandVisualStyleMediaProps) => {
  const { data: brandKitData, isLoading, isFetched } = useBrandKit(brandKitId, { includeAllMediaFiles: true })
  const [showAllMediaFiles, setShowAllMediaFiles] = useState(false)
  const [useStockMedia, setUseStockMedia] = useState(false)
  const [mediaPriority, setMediaPriority] = useState<BrandKitMediaPriority>('brand_kit_first')
  const [isUploading, setIsUploading] = useState(false)
  const { mutateAsync: updateBrandKitMutation } = useUpdateBrandKit({ id: brandKitId })
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const isOnboardingRedesignV2 = useIsOnboardingRedesignV2()

  const preferences = brandKitData?.brand_kit?.content_preferences

  useEffect(() => {
    if (!preferences) return

    if (!('image_media_priority' in preferences)) {
      setUseStockMedia(true)
      setMediaPriority('brand_kit_first')
    } else {
      const priority = preferences.image_media_priority || 'brand_kit_first'
      setUseStockMedia(priority === 'brand_kit_first')
      setMediaPriority(priority)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.image_media_priority])

  const handleUseStockMedia = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked
    setUseStockMedia(value)
  }

  const { mutate: deleteMediaFile } = useDeleteMediaFile({
    onSuccess: () => {
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
      queryClient.invalidateQueries({
        queryKey: ['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles', 'list'],
        exact: false,
      })
    },
    onError: () => {
      showErrorMessageToast('Could not delete media file.')
    },
  })

  const brandKitMediaFiles = brandKitData?.brand_kit.media_files || []
  const images = brandKitMediaFiles.filter((file) => file.mime_type?.startsWith('image'))
  const videos = brandKitMediaFiles.filter((file) => file.mime_type?.startsWith('video'))
  const mediaCount = brandKitMediaFiles.length

  const hasEnoughMedia = mediaCount >= 30
  const hasInsufficientMedia = mediaCount > 0 && mediaCount < 30
  const hasNoMedia = mediaCount === 0

  const updateMediaPreferences = (priority: BrandKitMediaPriority, callback: () => void) => {
    updateBrandKitMutation(
      {
        content_preferences: {
          ...brandKitData?.brand_kit?.content_preferences,
          image_media_priority: priority,
          video_media_priority: priority,
          ...(forVisualStyles ? { apply_visual_style: true } : {}),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
          callback()
        },
      },
    )
  }

  const handleNext = () => {
    const priority = useStockMedia ? 'brand_kit_first' : 'only_my_brand_kit'
    updateMediaPreferences(priority, () => onNext(false))
  }

  if (forVisualStyles && isOnboardingRedesignV2) {
    const handleVisualStylesNext = () => updateMediaPreferences(mediaPriority, () => onNext(true))
    const neededMedia = Math.max(0, 30 - mediaCount)

    return (
      <MultiStepStepLayout centerContent scrollContainerClassName={Styles.scrollContainer}>
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <div className={Styles.footerSide}>
                  <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                    Back
                  </Button>
                </div>
                <div className={Styles.footerSide}>
                  <Button
                    size="lg"
                    variant="primary"
                    onPress={handleVisualStylesNext}
                    aria-label="Continue to setup your brand font"
                    isDisabled={isUploading}
                  >
                    Continue to setup your brand font
                  </Button>
                </div>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <div className={cx(Styles.container, Styles.forVisualStyles)}>
            <div className={Styles.headingSection}>
              <div className={Styles.headingText}>
                <div className={Styles.mediaCountText}>
                  We detected {images.length} image{images.length !== 1 ? 's' : ''} and {videos.length} video
                  {videos.length !== 1 ? 's' : ''}.
                </div>
                <div className={Styles.mainHeading}>
                  <Heading level={1}>How much of your images you want to use?</Heading>
                </div>
              </div>
              <MediaPriorityToggle value={mediaPriority} onChange={setMediaPriority} isDisabled={isUploading} />
            </div>

            {brandKitMediaFiles.length > 0 && (
              <MediaFilesGrid
                mediaFiles={brandKitMediaFiles}
                showAllMediaFiles={showAllMediaFiles}
                onShowAllMediaFiles={setShowAllMediaFiles}
                onDelete={(id) => deleteMediaFile(id as number)}
                brandKitId={brandKitId}
              />
            )}

            <MediaQualityIndicator mediaCount={mediaCount} />

            <div className={Styles.uploadSection}>
              <UploadMoreButton
                brandKitId={brandKitId}
                onIsUploadingChange={setIsUploading}
                variant="primary"
                customText={`Upload ${neededMedia > 0 ? `${neededMedia} more` : 'more'} for ideal results`}
                className={Styles.uploadButton}
              />

              <GoogleDriveImport />
            </div>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  }

  const getTitle = () => {
    if (isLoading || !isFetched) {
      return `Loading your images and videos.`
    }
    if (hasNoMedia && !isLoading && isFetched) {
      return "We don't have any images to use for your content."
    }
    if (hasInsufficientMedia) {
      return (
        <span>
          We have <span className={Styles.bold}>{images.length || 'No'}</span> images and{' '}
          <span className={Styles.bold}>{videos.length || 'No'}</span> videos — That's not enough content for your
          marketing needs.
        </span>
      )
    }
    if (hasEnoughMedia) {
      return (
        <span>
          We have <span className={Styles.bold}>{images.length || 'No'}</span> images
          {!!videos.length && (
            <>
              {' '}
              and <span className={Styles.bold}>{videos.length || 'No'}</span> videos
            </>
          )}{' '}
          — enough content for roughly two months of posting.
        </span>
      )
    }
    return 'Add photos and videos to transform with your new style'
  }

  const title = getTitle()

  const footer = (
    <StickyFooter>
      <FooterButtons>
        <div className={Styles.footerSide}>
          <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
            Back
          </Button>
        </div>
        <div className={Styles.footerSide}>
          <Button
            size="lg"
            variant="secondary"
            onPress={handleNext}
            aria-label="Setup Your Channels"
            isDisabled={isUploading}
          >
            Continue to Setup Channels
          </Button>
        </div>
      </FooterButtons>
    </StickyFooter>
  )

  if (isLoading || !isFetched) {
    return (
      <MultiStepStepLayout title={title} centerContent scrollContainerClassName={Styles.scrollContainer}>
        <StickyFooterProvider footer={footer}>
          <div className={Styles.container}>
            <div className={Styles.mediaGrid}>
              {Array.from({ length: 10 }).map((_, index) => (
                <div className={cx(Styles.mediaItem, Styles.loadingMediaItem)} key={`loading-${index}`}>
                  <ImageThumbnail forceLoading image={{ url: '' }} />
                </div>
              ))}
            </div>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  }

  return (
    <MultiStepStepLayout title={title} centerContent scrollContainerClassName={Styles.scrollContainer}>
      <StickyFooterProvider footer={footer}>
        <div className={Styles.container}>
          {mediaCount > 0 && (
            <MediaFilesGrid
              mediaFiles={brandKitMediaFiles}
              showAllMediaFiles={showAllMediaFiles}
              onShowAllMediaFiles={setShowAllMediaFiles}
              onDelete={(id) => deleteMediaFile(id as number)}
              brandKitId={brandKitId}
            />
          )}

          {hasEnoughMedia && (
            <div className={Styles.useStockMediaContainer}>
              <ToggleButton checked={useStockMedia} name="use-stock-media" handleChange={handleUseStockMedia} />
              <Text>Use stock photography and videos when uploads run out</Text>
            </div>
          )}

          {hasNoMedia && (
            <div className={Styles.noMediaFiles}>
              <div className={Styles.uploadMoreContainer}>
                <Heading level={4}>
                  If you have large quantity of great photos and videos you can upload them below.
                </Heading>
                <img
                  src="https://res.cloudinary.com/almanac/image/upload/v1766521526/blaze_assets/virtual_marketer/onboarding/Frame_1171276974_a8fjdc.png"
                  alt="empty"
                  className={Styles.optionImage}
                />
              </div>
            </div>
          )}

          <div className={Styles.importButtons}>
            <GoogleDriveImport />
            <UploadMoreButton brandKitId={brandKitId} onIsUploadingChange={setIsUploading} />
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
